<template>
  <div class="user-roles__user-info" v-if="user" v-loading="isLoading">
    <div class="user-roles__user-info__header">
      <div class="user-roles__user-info__header-avatar">
        <div class="user-roles__user-info__header-avatar__wrapper">
          <img
            v-if="user && user.avatar"
            :src="getImageSource(user.avatar)"
            class="user-roles__user-info__header-avatar__image"
          />
        </div>
      </div>
      <div class="user-roles__user-info__header-name">
        <r-title>{{ user.name }}</r-title>
      </div>
      <div class="user-roles__user-info__control">
        <el-button
          type="primary"
          class="r-button"
          size="mini"
          @click="beforeCancel"
          v-if="hasChanges"
        >
          {{ $t("cancel") }}
        </el-button>
        <el-button
          type="success"
          class="r-button"
          size="mini"
          @click="beforeSaveUser"
          v-if="hasChanges"
        >
          {{ $t("save-changes") }}
        </el-button>
      </div>
      <el-button
        type="danger"
        class="r-button delete-user-btn"
        size="mini"
        @click="beforeDeleteUser"
      >
        {{ $t("delete-user") }}
      </el-button>
    </div>
    <div class="user-roles__user-info__content">
      <el-tabs class="r-tabs simple" v-model="activeModule">
        <el-tab-pane
          :name="item.name"
          v-for="item in filteredModulesList"
          :key="item.id"
        >
          <r-text slot="label" class="r-tabs__label">
            {{ $t(`user-info:module-name-${item.name}`) }}
          </r-text>
          <component
            v-if="item.name === 'local-admin'"
            v-bind:is="item.name"
            :show-password="true"
            :users="users"
            module="user-roles"
          />
          <component
            v-else
            v-bind:is="item.name"
            :show-password="true"
            module="user-roles"
          />
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import userInfo from "@/components/user-info/user-info";
import localAdmin from "../modules/local-admin/local-admin";
import modulesTools from "../modules/modules-tools/modules-tools";
import datasources from "../modules/datasources/datasources";
import { modulesList, userTemplate } from "../configs/";
import constants from "@/constants/url";
import moment from "moment";
import cloneDeep from "lodash.clonedeep";
import { notifyFactory, errorParser } from "@/utils";
import {
  userFields,
  userFieldsInfo
} from "@/components/user-info/configs/index";

export default {
  props: {
    users: {
      type: Array
    }
  },
  components: { userInfo, localAdmin, modulesTools, datasources },
  data() {
    return {
      downloadURL: "download_share_resource",
      baseURL: constants.URL,

      activeModule: "user-info",
      modulesList,
      userFields,
      userFieldsInfo
    };
  },
  computed: {
    isCreateUser() {
      return this.$store.state.userRoles.isCreateUser;
    },
    user() {
      return this.$store.state.userRoles.activeUser;
    },
    initialUser() {
      return this.$store.state.userRoles.initialUser;
    },
    isLoading() {
      return this.$store.state.userRoles.isLoading;
    },
    hasChanges() {
      const hasChanges = this.$store.state.userRoles.hasChanges;

      return (
        hasChanges.user ||
        hasChanges.modules ||
        hasChanges.datasources ||
        hasChanges.local_admin
      );
    },
    isAllFieldsFilled() {
      const { name, email, first_name, last_name, company } = this.user;

      return name && email && first_name && last_name && company;
    },
    userDS() {
      return this.$store.state.userRoles.activeUserDS;
    },
    userModules() {
      return this.$store.state.userRoles.activeUserModules;
    },
    isAdmin() {
      return this.$store.state.admin;
    },
    filteredModulesList() {
      return this.modulesList.filter(e => {
        if (e.name === "local-admin" && !this.isAdmin) return false;
        return true;
      });
    },
    isLocalAdmin() {
      return this.$store.state.userRoles.activeUser.local_admin || false;
    }
  },
  methods: {
    createUser() {
      this.$store.commit("SET_ACTIVE_USER_ROLES_USER", {
        user: { ...userTemplate },
        modules: [],
        ds: [],
        isCreate: true
      });
      this.$store.commit("SET_INITIAL_USER_ROLES_USER", {
        user: { ...userTemplate },
        modules: [],
        ds: []
      });
    },
    getImageSource(file) {
      return `${this.baseURL}/${this.downloadURL}/${file}`;
    },
    updateUser() {
      this.$store.commit("SET_INITIAL_USER_ROLES_USER", {
        user: this.user,
        modules: this.userModules,
        ds: this.userDS
      });
    },
    beforeCancel() {
      const title = this.$t("before:title");
      const message = this.$t("before-cancel:text");
      const confirmButtonText = this.$t("button-confirm");
      const cancelButtonText = this.$t("button-cancel");
      this.$confirm(message, title, {
        customClass: "r-message-box",
        closeOnPressEscape: true,
        closeOnClickModal: false,
        type: "warning",
        confirmButtonText,
        cancelButtonText
      })
        .then(() => {
          this.cancelChanges();
        })
        .catch(() => {});
    },
    async cancelChanges() {
      if (this.isCreateUser) {
        this.createUser();
      } else {
        this.$store.commit("SET_USER_ROLES_LOADING", true);
        this.cancelModulesChanges();
        this.cancelDSChanges();
        this.cancelUserChanges();
      }
    },
    cancelUserChanges() {
      this.userFields.find(e => {
        this.user[e.value] = this.initialUser[e.value];
      });
      this.userFieldsInfo.find(e => {
        this.user[e.value] = this.initialUser[e.value];
      });
      this.user.local_admin = this.initialUser.local_admin;
      this.user.managed_users = cloneDeep(this.initialUser.managed_users);
    },
    async cancelModulesChanges() {
      const { id } = this.user;
      const { data } = await this.$store.dispatch("POST_REQUEST", {
        url: `user/modules?user_id=${id}`
      });
      this.$store.state.userRoles.activeUserModules = data;
    },
    async cancelDSChanges() {
      const { id } = this.user;
      const datasources = await this.$store.dispatch("POST_REQUEST", {
        url: `user/datasources?user_id=${id}`
      });
      const userDS = datasources.data.map(item => ({
        ...item,
        _action: undefined,
        create: item.cruds[0],
        read: item.cruds[1],
        update: item.cruds[2],
        delete: item.cruds[3],
        share: item.cruds[4],
        export: item.cruds[5] || false
      }));
      userDS.forEach(ds => {
        if (ds.children) {
          ds.children.forEach(child => {
            userDS.push({
              ...child,
              _action: undefined,
              create: child.cruds[0],
              read: child.cruds[1],
              update: child.cruds[2],
              delete: child.cruds[3],
              share: child.cruds[4],
              export: child.cruds[5] || false
            });
          });
        }
      });
      this.$store.state.userRoles.activeUserDS = userDS;
      setTimeout(
        () => this.$store.commit("SET_USER_ROLES_LOADING", false),
        200
      );
    },
    async setActiveUser(user) {
      this.$store.commit("SET_USER_ROLES_LOADING", true);
      const { id } = user;
      const modules = await this.$store.dispatch("POST_REQUEST", {
        url: `user/modules?user_id=${id}`
      });
      const datasources = await this.$store.dispatch("POST_REQUEST", {
        url: `user/datasources?user_id=${id}`
      });

      const userModules = modules.data;
      const userDS = datasources.data.map(item => ({
        ...item,
        _action: undefined,
        create: item.cruds[0],
        read: item.cruds[1],
        update: item.cruds[2],
        delete: item.cruds[3],
        share: item.cruds[4],
        export: item.cruds[5] || false
      }));
      userDS.forEach(ds => {
        if (ds.children) {
          ds.children.forEach(child => {
            userDS.push({
              ...child,
              _action: undefined,
              create: child.cruds[0],
              read: child.cruds[1],
              update: child.cruds[2],
              delete: child.cruds[3],
              share: child.cruds[4],
              export: child.cruds[5] || false
            });
          });
        }
      });

      this.$store.commit("SET_ACTIVE_USER_ROLES_USER", {
        datasources: datasources.data,
        user,
        modules: userModules,
        ds: userDS
      });
      this.$store.commit("SET_INITIAL_USER_ROLES_USER", {
        datasources: datasources.data,
        user,
        modules: userModules,
        ds: userDS
      });
      this.userLoading = false;
      this.$store.commit("SET_USER_ROLES_LOADING", false);
    },
    beforeSaveUser() {
      if (!this.isAllFieldsFilled) {
        const title = this.$t("user-info:save-notify:title");
        const message = this.$t("user-info:save-notify:text");
        this.$notify(notifyFactory("error", title, message));
        return;
      }

      const title = this.$t("before:title");
      const message = this.$t("before-save:text");
      const confirmButtonText = this.$t("button-confirm");
      const cancelButtonText = this.$t("button-cancel");
      this.$confirm(message, title, {
        customClass: "r-message-box",
        closeOnPressEscape: true,
        closeOnClickModal: false,
        type: "warning",
        confirmButtonText,
        cancelButtonText
      })
        .then(() => {
          this.editUserData();
        })
        .catch(() => {});
    },
    async editUserData() {
      const mainConfig = { main_config: {} };

      mainConfig.user = this.user;

      if (this.isLocalAdmin) {
        mainConfig.main_config.datasources = this.userDS.map(item => ({
          ...item,
          cruds: [true, true, true, true, true, true]
        }));
      } else {
        mainConfig.main_config.datasources = this.userDS.map(item => ({
          ...item,
          cruds: [
            item.create,
            item.read,
            item.update,
            item.delete,
            item.share,
            item.export
          ]
        }));
      }
      mainConfig.user.valid_until = mainConfig.user.valid_until
        ? moment(mainConfig.user.valid_until).format()
        : null;
      delete mainConfig.user.unfilled_fields;
      if (!mainConfig.user.password) delete mainConfig.user.password;
      const data = {
        ...mainConfig.user,
        permissions: {
          modules: this.userModules,
          datasources: mainConfig.main_config.datasources
        }
      };
      if (data.managed_users.length) {
        data.managed_users = data.managed_users
          .filter(e => e.disabled || e.disabled === undefined)
          .map(e => {
            const user = { user_id: e.user_id };
            if (e.id) user.id = e.id;
            if (e.disabled !== undefined) user.disabled = !!e.disabled;
            return user;
          });
      }

      if (!this.isCreateUser) {
        try {
          this.$store.commit("SET_USER_ROLES_LOADING", true);
          await this.$store.dispatch("SAVE_USER_PERMS", data);
          const title = this.$t("user-info:edit-notify:title");
          const message = this.$t("user-info:edit-notify:text");
          this.$notify(notifyFactory("success", title, message));
          this.$store.commit("SET_USER_ROLES_USERS_UPDATE", true);
          this.$store.commit("SET_USER_ROLES_LOADING", false);
        } catch (error) {
          const title = this.$t("user-info:edit-notify:error-title");
          const message = this.$t("user-info:edit-notify:error-text");
          errorParser.call(this, error, message, title);
          this.$store.commit("SET_USER_ROLES_LOADING", false);
        }
      } else {
        try {
          this.$store.commit("SET_USER_ROLES_LOADING", true);
          await this.$store.dispatch("CREATE_USER", data);
          const title = this.$t("user-info:create-notify:title");
          const message = this.$t("user-info:create-notify:text");
          this.$notify(notifyFactory("success", title, message));
          this.updateUser();
          this.$store.commit("SET_USER_ROLES_USERS_UPDATE", true);
          this.$store.commit("SET_USER_ROLES_CHANGE_BY_NAME", data.name);
          this.$store.commit("SET_USER_ROLES_LOADING", false);
        } catch (error) {
          errorParser.call(this, error);
          this.$store.commit("SET_USER_ROLES_LOADING", false);
        }
      }
    },
    beforeDeleteUser() {
      const title = this.$t("before:title");
      const message = `${this.user.name} ${this.$t("before-delete:text")}`;
      const confirmButtonText = this.$t("button-confirm");
      const cancelButtonText = this.$t("button-cancel");
      this.$confirm(message, title, {
        customClass: "r-message-box",
        closeOnPressEscape: true,
        closeOnClickModal: false,
        type: "error",
        confirmButtonText,
        cancelButtonText
      })
        .then(() => {
          this.deleteUser();
        })
        .catch(() => {});
    },
    async deleteUser() {
      this.$store
        .dispatch("DELETE_REQUEST", {
          url: `objectInfo/auth.users?id=${this.user.id}`
        })
        .then(() => {
          const title = this.$t("user-info:delete-notify:title");
          const message = this.$t("user-info:delete-notify:text");
          this.$notify(notifyFactory("success", title, message));
          this.closeUser();
          this.$store.commit("SET_USER_ROLES_USERS_UPDATE", true);
        })
        .catch(error => {
          const title = this.$t("user-info:edit-notify:error-title");
          const message = this.$t("user-info:edit-notify:error-text");
          errorParser.call(this, error, message, title);
        });
    },
    closeUser() {
      this.$store.commit("SET_ACTIVE_USER_ROLES_USER", {
        user: null,
        modules: [],
        ds: []
      });
      this.$store.commit("SET_INITIAL_USER_ROLES_USER", {
        user: null,
        modules: [],
        ds: []
      });
    }
  }
};
</script>

<style lang="scss">
.user-roles__user-info {
  &__header {
    height: 50px;
    min-height: 50px;
    box-sizing: border-box;
    padding: 0 16px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    .r-button {
      &.el-button--success {
        background-color: #00b050 !important;
      }
    }
    &-avatar {
      height: 22px;
      width: 22px;
      overflow: hidden;
      border-radius: 50%;
      position: relative;
      margin-right: 8px;
      &__wrapper {
        height: 100%;
        width: 100%;
        background-color: var(--bg_containers) !important;
      }
      &__image {
        height: 100%;
        width: 100%;
        top: 50%;
        left: 50%;
        box-sizing: border-box;
      }
    }
    &-name {
      max-width: calc(100% - 480px);
      margin-right: 24px;
      box-sizing: border-box;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      .r-main-title {
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    .delete-user-btn {
      margin-left: auto;
    }
  }
  &__content {
    min-height: calc(100vh - 92px);
    height: 100%;
    width: 100%;
    position: relative;
    overflow: hidden;
  }
  .el-tab-pane {
    max-height: calc(100vh - 156px);
    overflow: auto;
  }
}
</style>

<i18n>
{
  "ru": {
    "user-info:module-name-user-info": "Информация о пользователе",
    "user-info:module-name-local-admin": "Управление пользователями",
    "user-info:module-name-modules-tools": "Модули и компоненты",
    "user-info:module-name-datasources": "Источники данных",

    "user-info:save-notify:title": "Необходимо заполнить все поля",
    "user-info:save-notify:text": "Заполните ФИО, данные об электронной почте и Вашей компании",

    "user-info:delete-notify:title": "Удаление пользователя",
    "user-info:delete-notify:text": "Удаление пользователя выполнено успешно",

    "user-info:edit-notify:title": "Редактирование пользователя",
    "user-info:edit-notify:text": "Редактирование пользователя выполнено успешно",
    "user-info:edit-notify:error-title": "Ошибка",
    "user-info:edit-notify:error-text": "Редактирование пользователя не выполнено",

    "user-info:create-notify:title": "Создание пользователя",
    "user-info:create-notify:text": "Создание пользователя выполнено успешно",

    "before:title": "Предупреждение",
    "before-save:text": "Все изменения для текущего пользователя будут сохранены. Вы уверены?",
    "before-delete:text": "- пользователь будет удален. Вы уверены?",
    "before-cancel:text": "Все изменения для текущего пользователя будут отменены. Вы уверены?",
    "button-confirm": "Да",
    "button-cancel": "Отмена",

    "delete-user": "Удалить пользователя"
  },
  "en": {
    "user-info:module-name-user-info": "User information",
    "user-info:module-name-local-admin": "User management",
    "user-info:module-name-modules-tools": "Modules and components",
    "user-info:module-name-datasources": "Data sources",

    "user-info:save-notify:title": "All fields are required.",
    "user-info:save-notify:text": "Fill in the full name, data about e-mail and your company",

    "user-info:delete-notify:title": "Delete user",
    "user-info:delete-notify:text": "User deletion successful",

    "user-info:edit-notify:title": "User editing",
    "user-info:edit-notify:text": "User editing completed successfully",
    "user-info:edit-notify:error-title": "Error",
    "user-info:edit-notify:error-text": "User editing failed",

    "user-info:create-notify:title": "User creating",
    "user-info:create-notify:text": "User creating completed successfully",

    "before:title": "Warning",
    "before-save:text": "All changes for the current user will be saved. Are you sure?",
    "before-delete:text": "- user will be deleted. Are you sure?",
    "before-cancel:text": "All changes for the current user will be discarded. Are you sure?",
    "button-confirm": "Yes",
    "button-cancel": "Cancel",

    "delete-user": "Delete user"
  }
}
</i18n>
